/**
 * §11.2 Live Status — MOCKED status band.
 *
 * ⚠️ The status here is not sourced from a real health API — it is a
 * static, hard-coded "all systems operational" view for the marketing
 * site.  A dedicated public Status page will be introduced in a later
 * release, at which point the incident-history chip will point to it.
 *
 * Renders:
 *   • Global rollup pill (green + pulse)
 *   • Four regional cards (Global / AP / EU / US)
 *   • Four uptime metrics (90d, 12m, Sev-1 count, MTTR)
 *   • "No incidents · trailing 90 days" info chip
 */

import React from 'react'
import { Activity, Globe, ShieldCheck } from 'lucide-react'
import { Container, Eyebrow, SectionHeading } from '@/components/landing/design-system'
import { STATUS_REGIONS, STATUS_METRICS } from '../data'

export default function LiveStatus() {
  return (
    <section id="status" className="relative py-20 md:py-24 scroll-mt-24">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
      <Container size="wide" className="flex flex-col gap-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="flex flex-col gap-4 max-w-2xl">
            <Eyebrow tone="green">Live Status</Eyebrow>
            <SectionHeading
              title={<>Every region,{' '}<span className="bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">green.</span></>}
              subtitle="A live rollup of the Kalki Ai Cloud production fabric.  A full incident history and per-service breakdown will land with the dedicated Status page."
            />
          </div>
          <span className="inline-flex items-center gap-2 self-start rounded-full border border-emerald-400/25 bg-emerald-400/[0.06] px-3 py-1.5 text-[11.5px] font-semibold text-emerald-300">
            <ShieldCheck className="h-3.5 w-3.5" /> No incidents · trailing 90 days
          </span>
        </div>

        {/* Regional grid */}
        <div className="grid gap-3 md:gap-4 grid-cols-2 lg:grid-cols-4">
          {STATUS_REGIONS.map((r) => (
            <div key={r.id} className="relative rounded-2xl border border-emerald-400/15 bg-emerald-400/[0.03] p-5 backdrop-blur-xl overflow-hidden">
              <div className="pointer-events-none absolute inset-0 opacity-60"
                style={{ background: 'radial-gradient(80% 40% at 20% 0%, rgba(34,197,94,0.10), transparent 70%)' }} />
              <div className="relative flex items-start justify-between gap-3">
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center gap-2 text-[11px] text-emerald-300 font-mono">
                    <Globe className="h-3 w-3" /> {r.region}
                  </div>
                  <div className="text-[14px] font-semibold text-white">{r.label}</div>
                </div>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/30 bg-emerald-400/[0.08] px-2 py-0.5 text-[10.5px] font-semibold text-emerald-300">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  operational
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Metrics rollup */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 md:p-6 backdrop-blur-xl">
          <div className="flex items-center gap-2 mb-4 text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">
            <Activity className="h-3.5 w-3.5 text-emerald-300" /> Reliability rollup
          </div>
          <div className="grid gap-4 md:gap-6 grid-cols-2 lg:grid-cols-4">
            {STATUS_METRICS.map((m) => (
              <div key={m.id} className="flex flex-col gap-1.5">
                <div className="text-2xl md:text-3xl font-semibold tracking-tight text-white">{m.value}</div>
                <div className="text-[11.5px] uppercase tracking-wider text-zinc-500">{m.label}</div>
              </div>
            ))}
          </div>
        </div>
      </Container>
    </section>
  )
}
